import React, { useContext, useState, useEffect, FunctionComponent, SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// d.ts
import { IDataController, DCRecord } from '@/@types/lib/dataController';

// Custom Components
import { generateIzletDocument } from '@/components/PdfDocuments/IzletDocument';

// UI
import ModelTable from '@/ui/Table/ModelTable';
import DialogContext from '@/ui/DialogContext/DialogContext';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import TableButtonGeneratePdf from '@/ui/Table/ActionButtons/TableButtonGeneratePdf';

// Lib
import dataController from '@/lib/dataController';

// Context
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';

// Types
import { LoaderContextType } from '@/@types/context/LoaderContext';
import { SnackbarContextType } from '@/@types/ui/SnackbarContext';
import { IModel } from '@/@types/models/model';
import { DialogContextType, IDialogProps } from '@/@types/ui/DialogContext';
import { ClosingDetails } from '@/@types/components/formController';
import { DialogFormGenerateFunction } from './DialogFormGenerateFunctionType';

import modelUser from '@/models/user';
import TableButtonDownloadExcel from '@/ui/Table/ActionButtons/TableButtonDownloadExcel';
import UserContext from '@/components/UserContext/UserContext';
import CreateExcelDialog from './CreateExcelDialog';
import TableButtonShareExcursion from '@/ui/Table/ActionButtons/TableButtonShareExcursion';
import api from '@/lib/api';
import PublicShareDialog from '@/ui/PublicPage/PublicShareDialog';
import { sifrarnici } from '@/lib/sifrarnici';
import TableButton from '@/ui/Table/ActionButtons/TableButton';
import TableButtonToggleJavno from '@/ui/Table/ActionButtons/TableButtonToggleJavno';
import TableButtonApprove from '@/ui/Table/ActionButtons/TableButtonApprove';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import TableButtonToggleOdrzan from '@/ui/Table/ActionButtons/TableButtonToggleOdržano';

// TODO:
// subModels cleanup: they are added to the project because Table.jsx can not function without them

interface IDataCentricPrimaryTableProps {
  onLoad?: (records: Array<DCRecord>) => void;
  onRecordSelect: (id: number) => void;
  onRowAction: (action: string, id: number, record?: DCRecord) => void;

  selectedRecordId?: number;
  view: string | undefined;

  model: IModel;
  titleToken: string;

  allowAdd?: boolean;
  addLabel?: string;
  allowView?: boolean;
  allowEdit?: boolean;
  allowGeneratePDF?: boolean;
  allowGenerateVodickiObrazac?: boolean;

  allowToggleJavno?: boolean;
  allowApprove?: boolean;
  allowToggleOdrzan?: boolean;
  izletiAdminFlagChangeType?: 'spv' | 'pu';

  dialogComponent?: React.FunctionComponent<IDialogProps>;
  dialogForm?: string | DialogFormGenerateFunction;
  dialogInsertForm?: string;
  dialogAsPagePath?: string | undefined;
  allowCanEditRecordAttribute?: string;
  allowCanExportPdfRecordAttribute?: string;
  allowShareIzlet?: boolean;

  exportLabel?: string;

  apiURL?: string;
  baseRecordPath: string;
}

const DataCentricPrimaryTable: FunctionComponent<React.PropsWithChildren<IDataCentricPrimaryTableProps>> = (props) => {
  const userContext = useContext(UserContext);
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { onLoad, onRowAction, onRecordSelect } = props;
  const {
    model,
    titleToken,
    view,
    selectedRecordId,
    apiURL,
    baseRecordPath,
    allowCanEditRecordAttribute,
    allowCanExportPdfRecordAttribute,
    allowGenerateVodickiObrazac,
    allowShareIzlet,
    izletiAdminFlagChangeType,
    allowToggleJavno,
    allowToggleOdrzan,
    allowApprove,
    exportLabel,
  } = props;
  const { allowAdd, addLabel, allowView, allowEdit, allowGeneratePDF, dialogComponent, dialogForm, dialogAsPagePath, dialogInsertForm } =
    props;

  const [records, setRecords] = useState<DCRecord[]>([]);

  useEffect(() => {
    refreshRecords();
  }, []);

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData(apiURL) // apiURl can be undefined for default model url fetch
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (resp.data.length === 0) {
            // snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
        if (onLoad) onLoad(records);
      });
  };

  const apiInstance = new api();

  const dc: IDataController = new dataController(model, baseRecordPath);
  const dcUser: IDataController = new dataController(modelUser);
  // const dialogContext = useContext<DialogContextType>(DialogContext);

  const handleView = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: 'view',
        form: form,
        recordId: record.id,
        onClose: (result: ClosingDetails) => {
          handleDialogClose({ dataChanged: result.dataChanged, action: 'view', selId: selId });
        },
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), { state: { view: 'view', form: form } });
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id && dialogAsPagePath === undefined) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: 'update',
        form: form,
        recordId: record.id,
        onClose: () => {
          handleDialogClose({ dataChanged: true, action: 'update', selId: selId });
        },
      });
    } else if (record !== undefined && record?.id && dialogAsPagePath !== undefined) {
      navigate(dialogAsPagePath + String(record.id), { state: { view: 'update', form: form } });
    }
  };

  const handleAdd = () => {
    if (dialogComponent) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: 'insert',
        form: dialogInsertForm || 'insert',
        onClose: () => {
          handleDialogClose({ dataChanged: true, action: 'insert' });
        },
      });
    }
  };

  const handleDownloadPDF = (evt: SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    /*if (record.status_id !== 30) {
      snackbarContext.showNotification('messages.izlet_pdf_condition', 'warning');
      return;
    }*/

    if (record.id) {
      loaderContext.toggleLoading(true);
      const showPreview = !(record.status_id === sifrarnici.status_izleta.approved)
      const onFinishCallback = () => loaderContext.toggleLoading(false);
      generateIzletDocument(record.id as number, onFinishCallback, showPreview);
    }
  };

  const handleDialogClose = (result: { dataChanged: boolean; action: string; selId?: number }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const handleCanEnableEditRecord = (selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    return allowCanEditRecordAttribute ? !!record[allowCanEditRecordAttribute] : true;
  };

  const handleCanExportPDFRecord = (selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    return allowCanExportPdfRecordAttribute ? !!record[allowCanExportPdfRecordAttribute] : true;
  };

  const handleGenerateVodickiObrazac = () => {
    if (userContext) {
      const filteredRecords = records.filter(x => x.status_id === sifrarnici.status_izleta.approved && x.is_odrzan === true);

      dialogContext.showDialog(CreateExcelDialog, {
        dc: dcUser,
        form: 'vodic_obrazac',
        initialRecord: {
          first_name: userContext.firstName,
          last_name: userContext.lastName,
          oib: userContext.oib,
          mobitel: userContext.mobitel,
          email: userContext.email,
          hps_plan_udruga_naziv: userContext.planUdrugaName,
          hps_spv_naziv: userContext.spvName,
          records: filteredRecords as [],
        },
      });
    }
  };

  const handleShareIzlet = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    const config = {
      position: undefined,
      extent: undefined,
      maplayers: undefined,
      route_points: undefined,
      obilaznica_id: undefined,
    };

    const object_id = record.id;

    const data = {
      config: JSON.stringify(config),
      object_id,
      object_type: 'izlet',
    };

    apiInstance.Call('share-generate', 'post', data).then((resp) => {
      // @ts-ignore
      if (resp.success && resp?.data?.url_hash) {
        // @ts-ignore
        const shareUrl = `${window.location.origin}/web-share?hash=${resp.data.url_hash}`;

        dialogContext.showDialog(PublicShareDialog, { url: shareUrl });
      }
    });
  };

  const handleStatusChangeApprove = (id: number, newStatusId: number) => {
    loaderContext.toggleLoading(true);
    dc.CustomActionOnRecord(id, 'approve', { approve: newStatusId === 30 })
      .then((response) => {
        if (response.success) {
          const msg = t('messages.izlet_status_change.' + newStatusId);
          snackbarContext.showNotification(msg, 'success');
          refreshRecords();
        } else {
          snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
        }
      })
      .catch((x) => {
        snackbarContext.showNotification(t('notifications.izlet_error.' + newStatusId), 'warning');
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleAproveWithWarning = (id: number, oldStatus: number) => {

    const newStatus = oldStatus === sifrarnici.status_izleta.approved ? sifrarnici.status_izleta.submitted : sifrarnici.status_izleta.approved

    if (newStatus === sifrarnici.status_izleta.submitted) {
       handleStatusChangeApprove(id, newStatus);
       return;
    }

    const title = t('cdialogs.are_you_sure_approve_izlet');
    const text = t('cdialogs.texts.approve_izlet');
    const confirmButtonText = t('cdialogs.continue');
    const cancelButtonText = t('cdialogs.cancel');

    dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText }).then((result) => {
      if (result.confirmed && !result.canceled) {
        handleStatusChangeApprove(id, newStatus);
      }
    });
  };

  const handleApprove = (evt: SyntheticEvent, selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    handleAproveWithWarning(record.id as number, record.status_id as number);
  };

  const handleToggleJavno = (evt: SyntheticEvent, selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    loaderContext.toggleLoading(true);
    const newIsJavno = !record.is_javno;
    dc.CustomActionOnRecord(record.id as number, 'is_javno', { is_javno: newIsJavno })
      .then((response) => {
        if (response.success) {
          const msg = t(`notifications.izlet.force_javno.${!newIsJavno}`);
          snackbarContext.showNotification(msg, 'success');
          refreshRecords();
        } else {
          snackbarContext.showNotification(t('notifications.error_javno'), 'warning');
        }
      })
      .catch((x) => {
        snackbarContext.showNotification(t('notifications.error_javno'), 'warning');
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleToggleOdrzan = (evt: SyntheticEvent, selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    loaderContext.toggleLoading(true);
    const newIsOdrzan = !record.is_odrzan;
    dc.CustomActionOnRecord(record.id as number, 'is_odrzan', { is_odrzan: newIsOdrzan })
      .then((response) => {
        if (response.success) {
          const msg = t(`notifications.izlet.toggle_odrzan.${!newIsOdrzan}`);
          snackbarContext.showNotification(msg, 'success');
          refreshRecords();
        } else {
          snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
        }
      })
      .catch((x) => {
        snackbarContext.showNotification(t('notifications.unexpected_error'), 'warning');
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const isEnabledApprove = (selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    return (
      (record.status_id === sifrarnici.status_izleta.submitted || record.status_id === sifrarnici.status_izleta.approved) &&
      isIzletFromMyUdruga(selection)
    );
  };

  const isEnabledOdrzan = (selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    return record.status_id === sifrarnici.status_izleta.approved && isIzletFromMyUdruga(selection);
  };

  const isIzletFromMyUdruga = (selection: string[]) => {
    const selId = Number(selection[0]);
    const record = records[selId];

    return (
      (izletiAdminFlagChangeType === 'pu' && record.organizator_udruga_id === userContext?.planUdrugaId) ||
      (izletiAdminFlagChangeType === 'spv' && record.organizator_udruga_id === userContext?.spvId)
    );
  };

  const javnoIconCondition = useCallback(
    (selection: string[]) => {
      const selId = Number(selection[0]);
      const record = records[selId];

      return !!record?.is_javno && isIzletFromMyUdruga(selection);
    },
    [records]
  );

  const odrzanIconCondition = useCallback(
    (selection: string[]) => {
      const selId = Number(selection[0]);
      const record = records[selId];

      return !!record?.is_odrzan && isIzletFromMyUdruga(selection);
    },
    [records]
  );

  const approveIconCondition = useCallback(
    (selection: string[]) => {
      const selId = Number(selection[0]);
      const record = records[selId];

      return record?.status_id === sifrarnici.status_izleta.approved && isIzletFromMyUdruga(selection);
    },
    [records]
  );

  const vodicikiObrazacButton = allowGenerateVodickiObrazac ? (
    <TableButtonDownloadExcel variant="outlined" allow="always" onClick={handleGenerateVodickiObrazac} />
  ) : null;

  const shareIzletButton = allowShareIzlet ? <TableButtonShareExcursion variant="outlined" onClick={handleShareIzlet} /> : null;

   const approveButton = allowApprove ? (
    <TableButtonApprove
      isEnabledRecord={isEnabledApprove}
      variant="outlined"
      onClick={handleApprove}
      TrueIcon={CheckBoxIcon}
      FalseIcon={CheckBoxOutlineBlankIcon}
      iconCondition={approveIconCondition}
    />
  ) : null;

  const toggleJavnoButton = allowToggleJavno ? (
    <TableButtonToggleJavno
      iconCondition={javnoIconCondition}
      TrueIcon={CheckBoxIcon}
      FalseIcon={CheckBoxOutlineBlankIcon}
      isEnabledRecord={isEnabledOdrzan}
      variant="outlined"
      onClick={handleToggleJavno}
    />
  ) : null;

 
  const toggleOdrzanButton = allowToggleJavno ? (
    <TableButtonToggleOdrzan
      /*tooltip={t("tooltips.approve_excursion")}*/ iconCondition={odrzanIconCondition}
      TrueIcon={CheckBoxIcon}
      FalseIcon={CheckBoxOutlineBlankIcon}
      isEnabledRecord={isEnabledOdrzan}
      variant="outlined"
      onClick={handleToggleOdrzan}
    />
  ) : null;

  return (
    <ModelTable
      tableName={model.title}
      title={t(titleToken)}
      records={records}
      dc={dc}
      allowSelection="one"
      allowFilter
      allowExport
      allowAdd={allowAdd}
      handleAdd={handleAdd}
      addLabel={addLabel || t('buttons.new')}
      onRowAction={onRowAction}
      onRowClick={(id: number) => onRecordSelect(id)}
      initialPageSize={10}
      allowRowAction={() => true}
      viewName={view}
      rowSelectedCustom={selectedRecordId}
      exportLabel={exportLabel}
    >
      {approveButton}
      {toggleJavnoButton}
      {toggleOdrzanButton}
      {vodicikiObrazacButton}
      {shareIzletButton}
      {allowGeneratePDF ? (
        <TableButtonGeneratePdf isEnabledRecord={handleCanExportPDFRecord} variant="outlined" onClick={handleDownloadPDF} />
      ) : null}
      {allowView ? <TableButtonView variant="contained" onClick={handleView} /> : null}
      {allowEdit ? <TableButtonEdit isEnabledRecord={handleCanEnableEditRecord} variant="contained" onClick={handleEdit} /> : null}
    </ModelTable>
  );
};

export default DataCentricPrimaryTable;
